import type { MeetingListResponse } from '@ai-meeting/shared'
import { fetchMeetingList } from '@/services/meetings'

const MEETING_NAME_PREFIX = 'Meeting#'

/**
 * How many meetings the account has ever had, from a list response that may
 * not carry the answer.
 *
 * `ownedTotalIncludingDeleted` is typed as required, so nothing in the editor
 * suggests it can be missing — and a server deployed before it existed sends
 * no such field. `undefined + 1` is `NaN`, `Math.max(1, NaN)` is `NaN`, and
 * the reader is offered a meeting called `Meeting#NaN`. That is not a
 * hypothetical: it is what a new front end talking to an old back end does,
 * which is every rolling deploy for as long as it lasts.
 *
 * Falls back to `ownedTotal`, which such a server does send, and then to
 * whatever the caller already knew.
 *
 * @param response The list response, or part of one.
 * @param fallback Used when the response carries neither count.
 * @returns A finite count, never `NaN`.
 */
export function readOwnedMeetingCount(
  response: Partial<Pick<MeetingListResponse, 'ownedTotal' | 'ownedTotalIncludingDeleted'>>,
  fallback = 0
): number {
  const counted = [response.ownedTotalIncludingDeleted, response.ownedTotal].find((value) =>
    Number.isFinite(value)
  )
  return counted ?? fallback
}

/**
 * Build the `Meeting#N` candidate for a given index.
 *
 * @param index One-based sequence number shown to the user.
 */
function buildMeetingName(index: number): string {
  return `${MEETING_NAME_PREFIX}${index}`
}

/**
 * Resolve a default meeting name from how many meetings the user owns.
 * Fetches the meeting list once and returns
 * `Meeting#{ownedTotalIncludingDeleted+1}`.
 *
 * Counting the trash is the whole point. A soft-deleted meeting keeps its
 * title — `assertTitleAvailable` refuses a name it is using — but drops off
 * every list the user can see. Counting only what is listed walks the number
 * backwards the moment a recent meeting is deleted: own `Meeting#1`..`#5`,
 * delete `#5`, and the next suggestion is `#5` again, which the server then
 * refuses with a name the user cannot find anywhere.
 *
 * If two meetings named `Meeting#N` briefly collide (e.g. two open tabs), the
 * backend `assertTitleAvailable` check inside `POST /api/meetings` remains the
 * final guard and a 409 surfaces the inline duplicate-title error to the user.
 *
 * Falls back to `Meeting#{fallbackCount + 1}` when the list fetch fails, so
 * the modal never gets stuck without a suggested name.
 *
 * @param fallbackCount Local saved-meeting count used if the fetch fails.
 * @returns A default meeting name candidate (subject to 409 on submit).
 */
export async function resolveDefaultMeetingName(fallbackCount = 0): Promise<string> {
  try {
    const response = await fetchMeetingList({
      limit: 1,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
    })
    return buildMeetingName(Math.max(1, readOwnedMeetingCount(response, fallbackCount) + 1))
  } catch {
    return buildMeetingName(Math.max(1, fallbackCount + 1))
  }
}
